// src/pages/admin/Feedback.jsx - COMPLETE FIXED VERSION
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { adminApi } from '../../api/adminApi';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  Chip,
  Paper,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Snackbar,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Avatar,
  Divider,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
  Stack,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Badge,
  CardActionArea,
  InputAdornment, // ✅ ADDED
} from '@mui/material';
import {
  Refresh,
  Search,
  Visibility,
  Delete,
  Edit,
  Close,
  CheckCircle,
  Schedule,
  Pending,
  Done,
  Warning,
  Info,
  Clear,
  Download,
  FilterList,
  Person,
  Storefront,
  Receipt,
  Email,
  Phone,
  Comment,
  Flag,
  Category,
  PriorityHigh,
  AccessTime,
  Check,
  ArrowBack,
  Send,
  Star,
  StarBorder,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// ============================================================
// CONSTANTS
// ============================================================
const FEEDBACK_TYPES = [
  { value: 'all', label: 'All Types' },
  { value: 'complaint', label: 'Complaint', icon: '⚠️' },
  { value: 'suggestion', label: 'Suggestion', icon: '💡' },
  { value: 'feedback', label: 'Feedback', icon: '📝' },
  { value: 'inquiry', label: 'Inquiry', icon: '❓' },
  { value: 'compliance', label: 'Compliance Issue', icon: '📋' },
  { value: 'praise', label: 'Praise', icon: '🌟' },
  { value: 'bug_report', label: 'Bug Report', icon: '🐛' },
  { value: 'feature_request', label: 'Feature Request', icon: '🚀' },
  { value: 'other', label: 'Other', icon: '📌' },
];

const FEEDBACK_STATUSES = [
  { value: 'all', label: 'All Status' },
  { value: 'pending', label: 'Pending', color: '#FF9800' },
  { value: 'in-progress', label: 'In Progress', color: '#2196F3' },
  { value: 'resolved', label: 'Resolved', color: '#4CAF50' },
  { value: 'closed', label: 'Closed', color: '#9E9E9E' },
];

const FEEDBACK_CATEGORIES = [
  { value: 'all', label: 'All Categories' },
  { value: 'general', label: 'General' },
  { value: 'order', label: 'Order' },
  { value: 'payment', label: 'Payment' },
  { value: 'product', label: 'Product' },
  { value: 'delivery', label: 'Delivery' },
  { value: 'account', label: 'Account' },
  { value: 'vendor', label: 'Vendor' },
  { value: 'technical', label: 'Technical' },
  { value: 'billing', label: 'Billing' },
  { value: 'returns', label: 'Returns & Refunds' },
  { value: 'commission', label: 'Commission' },
  { value: 'other', label: 'Other' },
];

const PRIORITY_LEVELS = [
  { value: 'all', label: 'All Priority' },
  { value: 'low', label: 'Low', color: '#4CAF50' },
  { value: 'medium', label: 'Medium', color: '#FF9800' },
  { value: 'high', label: 'High', color: '#F44336' },
  { value: 'urgent', label: 'Urgent', color: '#D32F2F' },
];

// ============================================================
// HELPER FUNCTIONS
// ============================================================
const getStatusColor = (status) => {
  const found = FEEDBACK_STATUSES.find(s => s.value === status);
  return found?.color || '#9E9E9E';
};

const getTypeLabel = (type) => {
  const found = FEEDBACK_TYPES.find(t => t.value === type);
  return found?.label || type;
};

const getTypeIcon = (type) => {
  const found = FEEDBACK_TYPES.find(t => t.value === type);
  return found?.icon || '📌';
};

const getCategoryLabel = (category) => {
  const found = FEEDBACK_CATEGORIES.find(c => c.value === category);
  return found?.label || category;
};

const getPriorityLabel = (priority) => {
  const found = PRIORITY_LEVELS.find(p => p.value === priority);
  return found?.label || priority;
};

const getPriorityColor = (priority) => {
  const found = PRIORITY_LEVELS.find(p => p.value === priority);
  return found?.color || '#9E9E9E';
};

const formatDate = (date) => {
  if (!date) return 'N/A';
  return new Date(date).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// ============================================================
// MAIN COMPONENT
// ============================================================
const AdminFeedback = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [feedbacks, setFeedbacks] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    inProgress: 0,
    resolved: 0,
    closed: 0,
    byType: {},
    byPriority: { low: 0, medium: 0, high: 0, urgent: 0 },
    byCategory: {},
  });
  const [selectedTab, setSelectedTab] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(isMobile ? 5 : 10);
  const [totalFeedbacks, setTotalFeedbacks] = useState(0);
  const [filters, setFilters] = useState({
    status: 'all',
    type: 'all',
    category: 'all',
    priority: 'all',
    search: '',
  });
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [openDetailDialog, setOpenDetailDialog] = useState(false);
  const [openStatusDialog, setOpenStatusDialog] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // ============================================================
  // FETCH DATA
  // ============================================================

  const fetchFeedback = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        status: filters.status !== 'all' ? filters.status : undefined,
        type: filters.type !== 'all' ? filters.type : undefined,
        category: filters.category !== 'all' ? filters.category : undefined,
        priority: filters.priority !== 'all' ? filters.priority : undefined,
        search: filters.search || undefined,
      };

      const response = await adminApi.getAllFeedback(params);
      const data = response.data || response || {};
      const feedbackList = data.feedbacks || data.data || [];

      setFeedbacks(feedbackList);
      setTotalFeedbacks(data.total || 0);
      setStats(data.stats || {
        total: 0,
        pending: 0,
        inProgress: 0,
        resolved: 0,
        closed: 0,
        byType: {},
        byPriority: { low: 0, medium: 0, high: 0, urgent: 0 },
        byCategory: {},
      });
    } catch (error) {
      console.error('Error fetching feedback:', error);
      showSnackbar('Failed to load feedback', 'error');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filters]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await adminApi.getFeedbackStats();
      const data = response.data || response || {};
      setStats(data.stats || {
        total: 0,
        pending: 0,
        inProgress: 0,
        resolved: 0,
        closed: 0,
        byType: {},
        byPriority: { low: 0, medium: 0, high: 0, urgent: 0 },
        byCategory: {},
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  useEffect(() => {
    fetchFeedback();
    fetchStats();
  }, [fetchFeedback, fetchStats]);

  useEffect(() => {
    setRowsPerPage(isMobile ? 5 : 10);
  }, [isMobile]);

  // ============================================================
  // HANDLERS
  // ============================================================

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(0);
  };

  const clearFilters = () => {
    setFilters({
      status: 'all',
      type: 'all',
      category: 'all',
      priority: 'all',
      search: '',
    });
    setPage(0);
  };

  const handleViewFeedback = (feedback) => {
    setSelectedFeedback(feedback);
    setOpenDetailDialog(true);
  };

  const handleOpenStatusDialog = (feedback) => {
    setSelectedFeedback(feedback);
    setNewStatus(feedback.status);
    setAdminNotes('');
    setOpenStatusDialog(true);
  };

  const handleUpdateStatus = async () => {
    if (!selectedFeedback || newStatus === selectedFeedback.status) {
      setOpenStatusDialog(false);
      return;
    }

    setSubmitting(true);
    try {
      await adminApi.updateFeedbackStatus(selectedFeedback.id, newStatus, adminNotes);
      showSnackbar(`Feedback status updated to ${getStatusLabel(newStatus)}`);
      setOpenStatusDialog(false);
      await fetchFeedback();
      await fetchStats();
      if (selectedFeedback && selectedFeedback.id === selectedFeedback.id) {
        setSelectedFeedback({ ...selectedFeedback, status: newStatus });
      }
    } catch (error) {
      console.error('Error updating status:', error);
      showSnackbar('Failed to update status', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteFeedback = async (id) => {
    if (!window.confirm('Are you sure you want to delete this feedback?')) return;

    try {
      await adminApi.deleteFeedback(id);
      showSnackbar('Feedback deleted successfully');
      await fetchFeedback();
      await fetchStats();
      if (selectedFeedback && selectedFeedback.id === id) {
        setOpenDetailDialog(false);
        setSelectedFeedback(null);
      }
    } catch (error) {
      console.error('Error deleting feedback:', error);
      showSnackbar('Failed to delete feedback', 'error');
    }
  };

  const getStatusLabel = (status) => {
    const found = FEEDBACK_STATUSES.find(s => s.value === status);
    return found?.label || status;
  };

  // ============================================================
  // RENDER COMPONENTS
  // ============================================================

  const renderStatCard = (label, value, color, icon) => (
    <Grid item xs={6} sm={4} md={2}>
      <Card
        sx={{
          height: '100%',
          borderLeft: `4px solid ${color}`,
          transition: 'all 0.2s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: theme.shadows[4],
          },
        }}
      >
        <CardContent sx={{ textAlign: 'center', py: { xs: 1.5, sm: 2 } }}>
          <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
            {icon && <Box sx={{ color }}>{icon}</Box>}
            <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight="bold" sx={{ color }}>
              {value || 0}
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {label}
          </Typography>
        </CardContent>
      </Card>
    </Grid>
  );

  const renderStatusChip = (status) => {
    const color = getStatusColor(status);
    const label = getStatusLabel(status);
    return (
      <Chip
        label={label}
        size="small"
        sx={{
          backgroundColor: color + '20',
          color: color,
          fontWeight: 600,
        }}
      />
    );
  };

  const renderPriorityChip = (priority) => {
    const color = getPriorityColor(priority);
    const label = getPriorityLabel(priority);
    return (
      <Chip
        label={label}
        size="small"
        sx={{
          backgroundColor: color + '20',
          color: color,
          fontWeight: 600,
        }}
      />
    );
  };

  // ============================================================
  // MAIN RENDER
  // ============================================================

  if (loading && feedbacks.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 4 }, pb: { xs: 10, sm: 12 } }}>
      {/* Header */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 3 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={2}
        >
          <Box>
            <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight="bold">
              Feedback Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage all user feedback, complaints, suggestions, and inquiries
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => { fetchFeedback(); fetchStats(); }}
              disabled={loading}
              size={isMobile ? 'small' : 'medium'}
            >
              Refresh
            </Button>
            <Button
              variant="outlined"
              startIcon={<Download />}
              size={isMobile ? 'small' : 'medium'}
            >
              Export
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Stats Cards */}
      <Grid container spacing={isMobile ? 1 : 2} sx={{ mb: 4 }}>
        {renderStatCard('Total', stats.total, theme.palette.primary.main, <Receipt fontSize="small" />)}
        {renderStatCard('Pending', stats.pending, '#FF9800', <Pending fontSize="small" />)}
        {renderStatCard('In Progress', stats.inProgress, '#2196F3', <Schedule fontSize="small" />)}
        {renderStatCard('Resolved', stats.resolved, '#4CAF50', <CheckCircle fontSize="small" />)}
        {renderStatCard('Closed', stats.closed, '#9E9E9E', <Close fontSize="small" />)}
        {renderStatCard('Urgent', stats.byPriority?.urgent || 0, '#D32F2F', <Warning fontSize="small" />)}
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search feedback..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: filters.search && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => handleFilterChange('search', '')}>
                      <Clear fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                label="Status"
              >
                {FEEDBACK_STATUSES.map((status) => (
                  <MenuItem key={status.value} value={status.value}>
                    {status.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select
                value={filters.type}
                onChange={(e) => handleFilterChange('type', e.target.value)}
                label="Type"
              >
                {FEEDBACK_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.icon} {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Priority</InputLabel>
              <Select
                value={filters.priority}
                onChange={(e) => handleFilterChange('priority', e.target.value)}
                label="Priority"
              >
                {PRIORITY_LEVELS.map((priority) => (
                  <MenuItem key={priority.value} value={priority.value}>
                    {priority.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
                label="Category"
              >
                {FEEDBACK_CATEGORIES.map((category) => (
                  <MenuItem key={category.value} value={category.value}>
                    {category.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {(filters.status !== 'all' ||
            filters.type !== 'all' ||
            filters.category !== 'all' ||
            filters.priority !== 'all' ||
            filters.search) && (
            <Grid item xs={12}>
              <Button size="small" onClick={clearFilters} startIcon={<Clear />}>
                Clear All Filters
              </Button>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Feedback Table */}
      <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead sx={{ bgcolor: 'grey.50' }}>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {feedbacks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No feedback found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                feedbacks.map((feedback) => (
                  <TableRow key={feedback.id} hover>
                    <TableCell>#{feedback.id}</TableCell>
                    <TableCell>
                      <Tooltip title={getTypeLabel(feedback.type)}>
                        <span style={{ fontSize: '20px' }}>{getTypeIcon(feedback.type)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                        {feedback.subject}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Avatar sx={{ width: 24, height: 24, bgcolor: 'primary.main' }}>
                          <Person sx={{ fontSize: 14 }} />
                        </Avatar>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 100 }}>
                          {feedback.User?.name || 'N/A'}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>{renderStatusChip(feedback.status)}</TableCell>
                    <TableCell>{renderPriorityChip(feedback.priority)}</TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(feedback.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="View Details">
                          <IconButton size="small" onClick={() => handleViewFeedback(feedback)}>
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Update Status">
                          <IconButton size="small" onClick={() => handleOpenStatusDialog(feedback)}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => handleDeleteFeedback(feedback.id)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalFeedbacks}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </Paper>

      {/* ============================================================ */}
      {/* DETAIL DIALOG */}
      {/* ============================================================ */}
      <Dialog
        open={openDetailDialog}
        onClose={() => setOpenDetailDialog(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedFeedback && (
          <>
            <DialogTitle>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h6">
                    Feedback #{selectedFeedback.id}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(selectedFeedback.createdAt)}
                  </Typography>
                </Box>
                <Box display="flex" gap={1}>
                  {renderStatusChip(selectedFeedback.status)}
                  {renderPriorityChip(selectedFeedback.priority)}
                </Box>
              </Stack>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Subject</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {selectedFeedback.subject}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Type</Typography>
                  <Typography variant="body1">
                    {getTypeIcon(selectedFeedback.type)} {getTypeLabel(selectedFeedback.type)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Category</Typography>
                  <Typography variant="body1">
                    {getCategoryLabel(selectedFeedback.category)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">User</Typography>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Avatar sx={{ width: 28, height: 28, bgcolor: 'primary.main' }}>
                      <Person sx={{ fontSize: 16 }} />
                    </Avatar>
                    <Box>
                      <Typography variant="body1">
                        {selectedFeedback.User?.name || 'N/A'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {selectedFeedback.User?.email || 'No email'}
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Order</Typography>
                  <Typography variant="body1">
                    {selectedFeedback.orderNumber || 'N/A'}
                  </Typography>
                </Grid>
                {selectedFeedback.Vendor && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Vendor</Typography>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Avatar sx={{ width: 28, height: 28, bgcolor: 'secondary.main' }}>
                        <Storefront sx={{ fontSize: 16 }} />
                      </Avatar>
                      <Typography variant="body1">
                        {selectedFeedback.Vendor?.storeName || 'N/A'}
                      </Typography>
                    </Stack>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Message</Typography>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', mt: 0.5 }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {selectedFeedback.message}
                    </Typography>
                  </Paper>
                </Grid>
                {selectedFeedback.adminNotes && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">Admin Notes</Typography>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'info.light', mt: 0.5 }}>
                      <Typography variant="body2">{selectedFeedback.adminNotes}</Typography>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 2, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
              <Button onClick={() => setOpenDetailDialog(false)}>Close</Button>
              <Button
                variant="contained"
                startIcon={<Edit />}
                onClick={() => {
                  setOpenDetailDialog(false);
                  handleOpenStatusDialog(selectedFeedback);
                }}
              >
                Update Status
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ============================================================ */}
      {/* STATUS UPDATE DIALOG */}
      {/* ============================================================ */}
      <Dialog
        open={openStatusDialog}
        onClose={() => setOpenStatusDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6">Update Feedback Status</Typography>
          <Typography variant="caption" color="text.secondary">
            Feedback #{selectedFeedback?.id}: {selectedFeedback?.subject}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3, mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Current Status
            </Typography>
            {selectedFeedback && renderStatusChip(selectedFeedback.status)}
          </Box>

          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>New Status</InputLabel>
            <Select
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
              label="New Status"
            >
              {FEEDBACK_STATUSES.filter(s => s.value !== 'all').map((status) => (
                <MenuItem key={status.value} value={status.value}>
                  {status.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedFeedback && newStatus !== selectedFeedback.status && (
            <Box sx={{ p: 2, bgcolor: 'warning.light', borderRadius: 1, mb: 2 }}>
              <Typography variant="caption" display="block" color="warning.dark">
                Changing status from <strong>{getStatusLabel(selectedFeedback.status)}</strong> to{' '}
                <strong>{getStatusLabel(newStatus)}</strong>
              </Typography>
            </Box>
          )}

          <TextField
            fullWidth
            multiline
            rows={3}
            label="Admin Notes (optional)"
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
            placeholder="Add notes about this status change..."
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button onClick={() => setOpenStatusDialog(false)} fullWidth={isMobile}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpdateStatus}
            disabled={!selectedFeedback || newStatus === selectedFeedback.status || submitting}
            fullWidth={isMobile}
          >
            {submitting ? <CircularProgress size={24} /> : 'Update Status'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============================================================ */}
      {/* SNACKBAR */}
      {/* ============================================================ */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AdminFeedback;